// useCoinData.ts

import { useEffect, useState } from 'react';
import axios from 'axios';
import { CoinProps } from '@/types/coins';

// ... interfaces are unchanged ...
interface HistoryItem {
    price: number;
    timestamp: number;
    dollar_toman_rate: number;
    price_in_toman: number;
}

interface Timeframes {
    [key: string]: boolean;
}

interface ExternalData {
    change: number;
    history: HistoryItem[];
    timeframes: Timeframes;
    /** "USD" when the series is in dollars (the world gold ounce) */
    quote_currency?: string;
    /** Market-closed hours (Unix seconds) inside this range, from the history API */
    market_closed_ranges?: { from: number; to: number }[];
}

interface UseCoinDataParams {
    initialCoin: CoinProps | undefined;
    initialTimePeriod: string;
    type?: 'crypto' | 'gold' | 'currency';
}

interface UseCoinDataReturn {
    history: ExternalData | null;
    timePeriod: string;
    updateTimePeriod: (newTimePeriod: string) => void;
    error: string | null;
    /** Range the current `history` / `error` belongs to; differs from `timePeriod` while a range loads */
    historyPeriod: string | null;
}

const useCoinData = ({
    initialCoin,
    initialTimePeriod,
    type = 'crypto',
}: UseCoinDataParams): UseCoinDataReturn => {
    const [history, setHistory] = useState<ExternalData | null>(null);
    const [timePeriod, setTimePeriod] = useState<string>(initialTimePeriod);
    const [error, setError] = useState<string | null>(null);
    const [historyPeriod, setHistoryPeriod] = useState<string | null>(null);

    useEffect(() => {
        let isActive = true;

        const symbolSlug = initialCoin?.symbol || initialCoin?.name;
        if (symbolSlug || initialCoin?.coinrankingUuid) {
            const fetchCoinData = async () => {
                try {
                    const response = await axios.get<ExternalData>(
                        `/api/coin/history/${symbolSlug?.toLowerCase()}/?time_frame=${timePeriod}&type=${type}`,
                    );

                    // 👇 2. Only update state if this effect is still the active one.
                    if (isActive) {
                        setHistory(response.data);
                        setError(null);
                        setHistoryPeriod(timePeriod);
                    }
                } catch (err: any) {
                    if (isActive) {
                        console.error('Failed to fetch coin data:', err);
                        setError(err.message || 'An unexpected error occurred');
                        setHistory(null);
                        setHistoryPeriod(timePeriod);
                    }
                }
            };

            fetchCoinData();
        }

        // 👇 3. This is the cleanup function.
        // It runs when the effect re-runs (e.g., when timePeriod changes).
        // It sets the flag to false, "cancelling" the previous request's ability to update state.
        return () => {
            isActive = false;
        };
    }, [timePeriod, initialCoin, type]); // I've removed redundant dependencies for clarity.

    // This function is now simpler as it only needs to update the timePeriod
    const updateTimePeriod = (newTimePeriod: string) => {
        setTimePeriod(newTimePeriod);
    };

    return { history, timePeriod, updateTimePeriod, error, historyPeriod };
};

export default useCoinData;
